import {
  arenaMotionScale,
  prefersReducedMotionFlash,
} from "../engine/MotionSettings.ts";
import { COLORS } from "./designTokens.ts";
import { FlashGovernor, type FlashRequestOpts } from "./FlashGovernor.ts";

/** The full-screen RED flashes (YOUR mainframe's alarm) — the photosensitivity
 *  hazard the BL-108 scoped reduced-motion carve-out zeroes. Non-red signal /
 *  upgrade / green flashes carry their own info and stay. Lower-cased once. */
const RED_FLASH = new Set([
  COLORS.ORACLE_RED.toLowerCase(),
  COLORS.MALWARE_RED.toLowerCase(),
]);

/** WOW Pass 2 (S190 item 4) — the SUSTAINED low-integrity ("near loss") edge
 *  tint. Distinct from the transient {@link ScreenEffects.vignette} (which
 *  decays): it persists while the mainframe is in the danger band, a slow
 *  breathing red edge glow (well under the WCAG 2.3.1 ≤3 Hz strobe line) that
 *  reads "you're in danger" without obscuring the board. The pulse is gated OFF
 *  under reduced motion / heavy arena load (steady tint, never a strobe); the
 *  base alpha is low so the play surface + the path stay clear. */
const DANGER_PULSE_HZ = 0.8;
const DANGER_BASE_ALPHA = 0.2;
const DANGER_PULSE_ALPHA = 0.12;
/** S195/BL-283 — the band-scaled base may deepen to at most this (base+pulse
 *  ≤ 0.44 — the documented photosensitivity bound; never full-screen). */
const DANGER_BASE_ALPHA_MAX = 0.32;

export class ScreenEffects {
  private shakeIntensity = 0;
  private shakeDuration = 0;
  private shakeTimer = 0;
  private shakeFreq = 30;
  private offsetX_ = 0;
  private offsetY_ = 0;
  // BL-238 — camera punch-in: a brief arena zoom-about-centre on hero moments
  // (boss kill, GOLD reached). Snaps to the peak then eases back to 1.0 quadratically
  // (mirrors the shake decay). Render-only; reduced-motion gates the peak to 1.
  private zoom_ = 1;
  private punchPeak = 1;
  private punchTimer = 0;
  private punchDuration = 0;
  // BL-239 — a brief chromatic-aberration "glitch burst" boost (added to the
  // arena's base chroma offset by the WebGL pass), decays quadratically.
  private chromaBurst_ = 0;
  private chromaPeak = 0;
  private chromaTimer = 0;
  private chromaDuration = 0;
  private flashAlpha = 0;
  private flashColor = "#ff0000";
  /** BL-108a — the photosensitivity governor + its render clock (ms). */
  private readonly governor = new FlashGovernor();
  private clock = 0;
  private vignetteAlpha = 0;
  private vignetteColor = "#ff0000";
  private vignetteTimer = 0;
  private vignetteDuration = 0;
  private vignetteGradCache: {
    color: string;
    w: number;
    h: number;
    grads: [CanvasGradient, CanvasGradient, CanvasGradient, CanvasGradient];
  } | null = null;
  /** BL-235 — cached board-depth vignette gradient (rebuilt on resize only). */
  private boardVignetteCache: {
    w: number;
    h: number;
    grad: CanvasGradient;
  } | null = null;
  /** WOW Pass 2 (item 4) — sustained low-integrity danger state + its clock. */
  private dangerActive = false;
  private dangerClock = 0;
  /** S195/BL-283 — the band-scaled danger base alpha (0.2 → 0.32). */
  private dangerBaseAlpha = DANGER_BASE_ALPHA;
  private dangerGradCache: {
    w: number;
    h: number;
    grads: [CanvasGradient, CanvasGradient, CanvasGradient, CanvasGradient];
  } | null = null;

  get offsetX(): number {
    return this.offsetX_;
  }
  get offsetY(): number {
    return this.offsetY_;
  }

  /** WOW Pass 2 (item 4) — toggle the sustained near-loss edge tint. S195/
   *  BL-283: now driven by the NearLossDirector (per-frame lives read, so an
   *  addLives rescue DISARMS it — it used to latch to run-end), with a
   *  band-scaled base alpha (deeper red as lives drop; the 0.8 Hz pulse and
   *  its ±0.12 swing stay constant — the ≤1 Hz photosensitivity rail). */
  setIntegrityDanger(active: boolean, baseAlpha: number = DANGER_BASE_ALPHA): void {
    const clamped = Math.min(baseAlpha, DANGER_BASE_ALPHA_MAX);
    if (active === this.dangerActive && clamped === this.dangerBaseAlpha) return;
    if (active !== this.dangerActive) this.dangerClock = 0;
    this.dangerActive = active;
    this.dangerBaseAlpha = clamped;
  }

  get integrityDangerActive(): boolean {
    return this.dangerActive;
  }

  /** Current alpha for the danger edge tint (0 when inactive). Pulses only when
   *  arena motion is allowed; steady (= base) under reduced motion / load. */
  get integrityDangerAlpha(): number {
    if (!this.dangerActive) return 0;
    if (arenaMotionScale() <= 0) return this.dangerBaseAlpha; // steady (no strobe)
    const phase = (this.dangerClock / 1000) * DANGER_PULSE_HZ * Math.PI * 2;
    const pulse = 0.5 + 0.5 * Math.sin(phase); // 0..1, slow breathing
    return this.dangerBaseAlpha + pulse * DANGER_PULSE_ALPHA;
  }

  /** Current decayed shake amplitude (the quadratic envelope update() renders
   *  from). 0 when idle. Exposed for the arbitration law + tests. */
  get shakeAmplitudeNow(): number {
    if (this.shakeTimer <= 0) return 0;
    const progress = this.shakeTimer / this.shakeDuration;
    return this.shakeIntensity * progress * progress;
  }

  shake(intensity = 6, duration = 300): void {
    // BL-45 + BL-44: reduced motion hard-kills shake (arenaMotionScale → 0);
    // otherwise the perf governor damps its amplitude GRADUALLY under arena load.
    const s = arenaMotionScale();
    // S195 anti-fight law #1 (MAX-COMMIT, BL-280): a request lands only when
    // it is at least as strong as what is still on screen — a marquee shake
    // (boss kill 2.6 px) can no longer be stomped by a smaller one (wave-clear
    // 2.2 px) arriving later in the same frame. Equal-or-stronger re-triggers,
    // so kill-storms keep their max envelope; same-frame order stops mattering.
    if (intensity * s < this.shakeAmplitudeNow) return;
    this.shakeIntensity = intensity * s;
    this.shakeDuration = duration;
    this.shakeTimer = s > 0 ? duration : 0;
    this.shakeFreq = 30;
  }

  /** BL-238 — camera punch-in. Briefly zooms the arena toward `scale` (about the
   *  canvas centre) and eases back to 1.0 over `durationMs`. Like shake(), the
   *  peak is damped by arenaMotionScale (reduced motion / load → no zoom). The
   *  zoom is render-only (applied + reset around the arena draw each frame) so it
   *  never persists and never touches pointer→cell hit-testing. */
  punch(scale = 1.12, durationMs = 140): void {
    const s = arenaMotionScale();
    const peak = 1 + (scale - 1) * s;
    if (peak <= 1.0001) return; // reduced motion / load → no punch
    this.punchPeak = peak;
    this.punchDuration = durationMs;
    this.punchTimer = durationMs;
  }

  /** Current render zoom (1 = none). Read by the render pipeline via the
   *  transform helpers; exposed for tests. */
  get zoom(): number {
    return this.zoom_;
  }

  /** BL-239 — a brief chromatic-aberration "glitch burst" on a hero moment, on
   *  TOP of the arena's base chroma. Like punch(), the peak is damped by
   *  arenaMotionScale (reduced motion / load → no burst). The boost is read by
   *  the WebGL chroma pass and only shows at the high-quality tier (it degrades
   *  off with the rest of post-FX under load). Photosensitivity-safe: chroma is
   *  a sub-pixel RGB displacement, not a luminance flash. */
  chromaPulse(peak = 0.008, durationMs = 200): void {
    const p = peak * arenaMotionScale();
    if (p <= 0) return;
    this.chromaPeak = p;
    this.chromaDuration = durationMs;
    this.chromaTimer = durationMs;
  }

  /** Current extra chroma offset (0 when idle) — the render pipeline adds it to
   *  the base chroma offset for the WebGL aberration pass. */
  get chromaBurst(): number {
    return this.chromaBurst_;
  }

  flash(color = "#ff0000", alpha = 0.3, opts: FlashRequestOpts = {}): void {
    // BL-108b — scoped reduced-motion carve-out: a `prefers-reduced-motion` user
    // gets the full-screen RED flash zeroed (the photosensitivity hazard). The
    // D-212 boot/arena CRT/glitch identity is exempt (never routes here); non-red
    // informational flashes still play; the calm red vignette still conveys the
    // breach, so no information is lost.
    if (RED_FLASH.has(color.toLowerCase()) && prefersReducedMotionFlash()) return;
    // BL-45 + BL-44: reduced motion damps the full-screen flash to 0; under arena
    // load the governor eases it gradually (continuous arenaMotionScale).
    const damped = alpha * arenaMotionScale();
    // BL-108a — the flash governor bounds the late-wave storm for EVERYONE
    // (stack-cap + ≤3 perceived flashes/sec). It only ever RAISES toward a fresh
    // pulse; a suppressed request leaves the in-flight flash untouched.
    const granted = this.governor.request(
      this.clock,
      damped,
      this.flashAlpha,
      opts,
    );
    if (granted > this.flashAlpha) {
      this.flashAlpha = granted;
      this.flashColor = color;
    }
  }

  vignette(color: string, duration: number): void {
    // S195 anti-fight law #2 (LONGEST-STANDING, BL-280): a request lands only
    // when it will outlast what is already burning — the boss-arrival red
    // (700 ms) survives the wave-clear green (650 ms) landing a frame later.
    // A fresh/equal-or-longer request still replaces (escalation upward).
    if (duration < this.vignetteTimer) return;
    this.vignetteColor = color;
    this.vignetteAlpha = 0.6;
    this.vignetteTimer = duration;
    this.vignetteDuration = duration;
  }

  /** Remaining burn of the transient vignette (0 = idle). For arbitration tests. */
  get vignetteRemainingMs(): number {
    return Math.max(0, this.vignetteTimer);
  }

  /** The colour currently burning in the transient vignette (null when idle). */
  get vignetteColorNow(): string | null {
    return this.vignetteTimer > 0 ? this.vignetteColor : null;
  }

  update(dt: number): void {
    // Render clock for the flash governor's rate window (BL-108a). Never sim time.
    this.clock += dt;
    if (this.shakeTimer > 0) {
      this.shakeTimer -= dt;
      if (this.shakeTimer <= 0) {
        this.offsetX_ = 0;
        this.offsetY_ = 0;
      } else {
        const progress = this.shakeTimer / this.shakeDuration;
        const decay = progress * progress; // quadratic decay
        const elapsed = this.shakeDuration - this.shakeTimer;
        const t = elapsed / 1000; // seconds
        const sine = Math.sin(t * this.shakeFreq * Math.PI * 2);
        this.offsetX_ = sine * this.shakeIntensity * decay;
        this.offsetY_ = sine * this.shakeIntensity * decay * 0.7;
      }
    } else {
      this.offsetX_ = 0;
      this.offsetY_ = 0;
    }

    if (this.flashAlpha > 0) {
      this.flashAlpha -= dt * 0.0015;
      if (this.flashAlpha < 0) this.flashAlpha = 0;
    }

    if (this.vignetteTimer > 0) {
      this.vignetteTimer -= dt;
      this.vignetteAlpha =
        0.6 * Math.max(0, this.vignetteTimer / this.vignetteDuration);
      if (this.vignetteTimer <= 0) this.vignetteAlpha = 0;
    }

    // WOW Pass 2 (item 4) — advance the near-loss breathing clock while active.
    if (this.dangerActive) this.dangerClock += dt;

    // BL-238 — ease the camera punch back to 1.0 (quadratic, like the shake).
    if (this.punchTimer > 0) {
      this.punchTimer -= dt;
      if (this.punchTimer <= 0) {
        this.punchTimer = 0;
        this.zoom_ = 1;
      } else {
        const f = this.punchTimer / this.punchDuration;
        this.zoom_ = 1 + (this.punchPeak - 1) * f * f;
      }
    }

    // BL-239 — ease the chroma glitch-burst back to 0 (quadratic).
    if (this.chromaTimer > 0) {
      this.chromaTimer -= dt;
      if (this.chromaTimer <= 0) {
        this.chromaTimer = 0;
        this.chromaBurst_ = 0;
      } else {
        const f = this.chromaTimer / this.chromaDuration;
        this.chromaBurst_ = this.chromaPeak * f * f;
      }
    }
  }

  /** Apply the live camera transform (zoom punch about centre + shake offset) to
   *  `ctx`. `width`/`height` (logical px) are needed only for the zoom centre;
   *  omit them (or when no punch is active) and this is the byte-identical
   *  translate-only shake of before. Pair with {@link resetTransform} using the
   *  SAME args — it reverses the exact operations (no save/restore needed). */
  applyTransform(ctx: CanvasRenderingContext2D, width?: number, height?: number): void {
    if (this.zoom_ !== 1 && width && height) {
      const cx = width / 2;
      const cy = height / 2;
      ctx.translate(cx, cy);
      ctx.scale(this.zoom_, this.zoom_);
      ctx.translate(-cx, -cy);
    }
    if (this.offsetX_ !== 0 || this.offsetY_ !== 0) {
      ctx.translate(this.offsetX_, this.offsetY_);
    }
  }

  resetTransform(ctx: CanvasRenderingContext2D, width?: number, height?: number): void {
    if (this.offsetX_ !== 0 || this.offsetY_ !== 0) {
      ctx.translate(-this.offsetX_, -this.offsetY_);
    }
    if (this.zoom_ !== 1 && width && height) {
      const cx = width / 2;
      const cy = height / 2;
      ctx.translate(cx, cy);
      ctx.scale(1 / this.zoom_, 1 / this.zoom_);
      ctx.translate(-cx, -cy);
    }
  }

  /** BL-235 — a subtle, PERSISTENT board-depth vignette: a cached radial that
   *  darkens the arena edges toward the corners (focus-pull to the centre), so
   *  the board reads with depth instead of flat. Static + cheap (one cached
   *  gradient fill) ⇒ calm-pass + reduced-motion safe; rgba (no governed token).
   *  Drawn over the board content, under the transient flash/vignette + HUD. */
  renderBoardVignette(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): void {
    let c = this.boardVignetteCache;
    if (!c || c.w !== width || c.h !== height) {
      const cx = width / 2;
      const cy = height / 2;
      const r = Math.max(width, height) * 0.72;
      const grad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, r);
      grad.addColorStop(0, "rgba(0,0,0,0)");
      grad.addColorStop(1, "rgba(0,0,0,0.28)");
      c = this.boardVignetteCache = { w: width, h: height, grad };
    }
    ctx.fillStyle = c.grad;
    ctx.fillRect(0, 0, width, height);
  }

  renderFlash(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): void {
    if (this.flashAlpha <= 0) return;
    ctx.fillStyle = this.flashColor;
    ctx.globalAlpha = this.flashAlpha;
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 1;
  }

  renderVignette(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): void {
    const edgeSize = Math.min(width, height) * 0.15;

    // WOW Pass 2 (item 4) — the SUSTAINED near-loss tint renders FIRST (under
    // the transient breach vignette), so a fresh hit still reads over the top.
    const dangerAlpha = this.integrityDangerAlpha;
    if (dangerAlpha > 0) {
      const d = this.dangerGradCache;
      if (!d || d.w !== width || d.h !== height) {
        this.dangerGradCache = {
          w: width,
          h: height,
          grads: this.buildEdgeGrads(ctx, COLORS.MALWARE_RED, width, height, edgeSize),
        };
      }
      this.fillEdges(ctx, width, height, edgeSize, this.dangerGradCache!.grads, dangerAlpha);
    }

    if (this.vignetteAlpha <= 0) return;

    // Cache gradients if color/dimensions changed
    const c = this.vignetteGradCache;
    if (
      !c ||
      c.color !== this.vignetteColor ||
      c.w !== width ||
      c.h !== height
    ) {
      this.vignetteGradCache = {
        color: this.vignetteColor,
        w: width,
        h: height,
        grads: this.buildEdgeGrads(
          ctx,
          this.vignetteColor,
          width,
          height,
          edgeSize,
        ),
      };
    }

    this.fillEdges(
      ctx,
      width,
      height,
      edgeSize,
      this.vignetteGradCache!.grads,
      this.vignetteAlpha,
    );
  }

  /** Build the four edge gradients (top/bottom/left/right) for an edge vignette
   *  of the given colour. Shared by the transient + sustained-danger passes. */
  private buildEdgeGrads(
    ctx: CanvasRenderingContext2D,
    color: string,
    width: number,
    height: number,
    edgeSize: number,
  ): [CanvasGradient, CanvasGradient, CanvasGradient, CanvasGradient] {
    const gradT = ctx.createLinearGradient(0, 0, 0, edgeSize);
    gradT.addColorStop(0, color);
    gradT.addColorStop(1, "transparent");
    const gradB = ctx.createLinearGradient(0, height, 0, height - edgeSize);
    gradB.addColorStop(0, color);
    gradB.addColorStop(1, "transparent");
    const gradL = ctx.createLinearGradient(0, 0, edgeSize, 0);
    gradL.addColorStop(0, color);
    gradL.addColorStop(1, "transparent");
    const gradR = ctx.createLinearGradient(width, 0, width - edgeSize, 0);
    gradR.addColorStop(0, color);
    gradR.addColorStop(1, "transparent");
    return [gradT, gradB, gradL, gradR];
  }

  /** Paint the four edge bands with the given gradients at the given alpha. */
  private fillEdges(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    edgeSize: number,
    grads: [CanvasGradient, CanvasGradient, CanvasGradient, CanvasGradient],
    alpha: number,
  ): void {
    const [gradT, gradB, gradL, gradR] = grads;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = gradT;
    ctx.fillRect(0, 0, width, edgeSize);
    ctx.fillStyle = gradB;
    ctx.fillRect(0, height - edgeSize, width, edgeSize);
    ctx.fillStyle = gradL;
    ctx.fillRect(0, 0, edgeSize, height);
    ctx.fillStyle = gradR;
    ctx.fillRect(width - edgeSize, 0, edgeSize, height);
    ctx.globalAlpha = 1;
  }
}
